import { nid } from '@astlai/common/util/nanoid';
import type { SourceBlock } from 'src/util/citation-transformer';

/** Tool call from the LLM during agentic loop, with optional result info */
export interface ToolCall {
	name: string;
	arguments: Record<string, unknown>;
	call_id: string;
	description?: string;
	/** Result count, set after tool execution completes */
	result_count?: number;
}

/** Topic extracted from the conversation */
export interface TopicInfo {
	topic_description: string;
}

/** Query classification from the classifier */
export interface ClassificationInfo {
	name: string;
	enable_thinking: boolean;
	enable_tools: boolean;
	temperature: number;
	max_iterations: number;
	thinking_budget: number;
}

export class ChatMessage {
	type?: 'message';
	status?:
		| 'pending'
		| 'rag.started'
		| 'rag.tool_call'
		| 'rag.tool_result'
		| 'rag.sources'
		| 'rag.web_sources'
		| 'reasoning'
		| 'in_progress'
		| 'completed'
		| 'incomplete';
	annotations?: any[];
	refusal?: boolean;

	id? = nid();

	/** Topic of the conversation (extracted post-hoc) */
	topic?: TopicInfo;

	/** Query classification result */
	classification?: ClassificationInfo;

	/** Current tool being called (index into toolCalls array) */
	currentToolCallIndex?: number;

	/** Tool calls with their results */
	toolCalls?: ToolCall[];

	timings?: {
		total_seconds: number;
		steps?: Record<string, number>;
	};

	/** Tools available when this message was sent (e.g. ['web_search']) */
	available_tools?: string[];

	created?: Date = new Date();

	constructor(
		public role: 'user' | 'assistant' | 'system',
		public content: string,
		public reasoning_content: string = ''
	) {}
}

export type ChatMessages = ChatMessage[];

/** Simplified source reference with just IDs (persisted format) */
export interface SourceDocument {
	doc_id: number;
	chunk_ids: number[];
}

export type WebSourceType = 'web' | 'fetched_url';

/** External source from web search or fetched URL (persisted format) */
export interface WebSourceDocument {
	id: string; // CRC32 hex hash of URL (8 chars), stable across fetches
	type: WebSourceType;
	title: string;
	url: string;
	description: string;
	content?: string; // Page content snippet (not displayed in UI, available for future agentic loops)
	fetched_at?: string;
}

/** A single citation occurrence (one badge) within a cited source */
export interface CitationOccurrence {
	hash: string;
	sub_index: string;
	quotedText?: string;
	chunkText?: string;
	sourceBlocks?: SourceBlock[];
	type: 'chunk' | 'document' | 'web';
	/** chunkId for 'chunk', docId for 'document', webId for 'web' */
	id: number | string;
}

/** Unified cited source — replaces QuotedDocument and QuotedWebSource */
export interface CitedSource {
	citation_index: number;
	citation_color: string;
	occurrences: CitationOccurrence[];
	title: string;
	// KB-specific
	doc_id?: number;
	chunk_ids?: number[];
	// Web-specific
	web_id?: string;
	url?: string;
	description?: string;
	fetched_at?: string;
	web_source_type?: WebSourceType;
}

/** Chunk data sent during streaming.
 * Document metadata available via doc_id lookup in RetrievalContext.documents.
 */
export interface ChunkDTO {
	id: number;
	text: string;
	doc_id: number;
	chunk_type: string;
	chunk_meta?: Record<string, unknown>;
}

/** Full retrieval context sent during streaming (not persisted) */
export interface RetrievalContext {
	documents: DocumentOverview[];
	chunks: ChunkDTO[];
}

export type ResourceType = 'debate' | 'person' | 'eu_cod' | 'cla' | 'kleine_anfrage';

export const RESOURCE_SORT_COLUMNS = ['name', 'type', 'date', 'created', 'document_count'] as const;
export type ResourceSortColumn = (typeof RESOURCE_SORT_COLUMNS)[number];

export type DocumentType =
	| 'speech'
	| 'politician_bio'
	| 'eu_com_proposal'
	| 'eu_ep_amendments_adopted'
	| 'eu_ep_cmt_opinion'
	| 'eu_egp_briefing'
	| 'cla_paragraph'
	| 'cla_annex'
	| 'press_message'
	| 'kleine_anfrage';

export interface ResourceWithDocuments {
	// Simplified property names (new format)
	id?: number;
	type?: ResourceType;
	name?: string;
	meta?: Record<string, any>;
	created?: string; // ISO date string from Python
	date?: string | null; // ISO date string from Python (actual resource date, e.g., debate date)
	document_count: number;
}

export interface ResourcesResponse {
	resources: ResourceWithDocuments[];
	total_resources: number;
	total_documents: number;
	chunk_coverage: Record<string, number>;
	page: number;
	per_page: number;
}

export interface DocumentOverview {
	id: number;
	type: string;
	meta?: Record<string, string>;
	res_name?: string;
	res_date?: string;
	doc_date?: string;
}

export interface ResourceRef {
	id: number;
	type: string;
	name: string;
	date?: string;
	href?: string;
	meta: Record<string, unknown>;
}

export interface DocumentDTO extends DocumentOverview {
	text: string;
	summary: string;
	meta: Record<string, string>;
	created: string;
	href?: string;
	resource?: ResourceRef;
	// @deprecated use resource.href now, this is only for bw compat for sw cached docs
	res_href?: string;
}

export interface TenantStats {
	total_resources: number;
	total_documents: number;
	by_resource_type: Record<
		string,
		{
			count: number;
			data_keys: string[];
			documents: Record<string, { count: number; meta_keys: string[] }>;
		}
	>;
}

export interface ResourceDTO {
	id: number;
	type: string;
	name: string;
	meta: Record<string, any>;
	created: string;
	date: string | null;
	documents: DocumentOverview[];
}
